import { useCallback, useEffect, useMemo, type MouseEvent, type PointerEvent } from 'react'
import { AevocamViewport } from './AevocamViewport'
import { useAevocamPlayer } from './useAevocamPlayer'
import './AevocamPlayer.css'
import { AevocamActionBar } from './AevocamActionBar'

/** Clicks on these keep their native focus (buttons, scrubber, form controls). */
const SHELL_FOCUS_SKIP_SELECTOR =
  'button, a, input, select, textarea, option, label, [role="slider"]'

export type AevocamPlayerProps = {
  width?: number,
  onHeightChange?: (height: number, photoWidth: number, photoHeight: number) => void,
}

export function AevocamPlayer({
  width,
  onHeightChange,
}: AevocamPlayerProps) {
  const {
    containerRef,
    playerShellRef,
    viewportRef,
    deferPlayPauseRef,
    currentIndex,
    loadingStage,
    totalPhotos,
    feed,
    currentPhoto,
    aspectRatio,
    actions,
  } = useAevocamPlayer()

  useEffect(() => {
    actions.applyExternalWidth(width)
  }, [width, actions.applyExternalWidth])

  //--------------------------------------------------------------------------------
  const formattedDateTime = useMemo(() => {
    if (!currentPhoto) return null

    const dateStr = currentPhoto.d
    const timeSlot = currentPhoto.t

    const parts = dateStr.split('-').map(Number)
    if (parts.length !== 3 || parts.some(isNaN)) {
      return dateStr
    }

    const year = parts[0]
    const month = parts[1]
    const day = parts[2]

    if (year === undefined || month === undefined || day === undefined) {
      return dateStr
    }

    const hours = Math.floor(timeSlot / 10000)
    const minutes = Math.floor((timeSlot % 10000) / 100)
    const seconds = timeSlot % 100

    const date = new Date(year, month - 1, day, hours, minutes, seconds)

    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: feed?.timezone || undefined,
    }

    return date.toLocaleString(undefined, options)
  }, [currentPhoto, feed?.timezone])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (width !== undefined && aspectRatio > 0 && onHeightChange && currentPhoto) {
      const calculatedHeight = Math.round(width * aspectRatio)
      onHeightChange(calculatedHeight, currentPhoto.w, currentPhoto.h)
    }
  }, [width, aspectRatio, onHeightChange, currentPhoto])

  //--------------------------------------------------------------------------------
  /**
   * Mousedown capture runs before child handlers and before we move focus. If focus is not
   * already inside {@link AevocamPlayerShell} (e.g. body or another page region), the next viewport
   * click only moves focus to the shell; if focus is on in-shell controls, play/pause runs on that click.
   */
  const handleFocusShellFromChromeCapture = useCallback(
    (e: MouseEvent<HTMLDivElement> | PointerEvent<HTMLDivElement>) => {
      const el = e.target
      if (!(el instanceof HTMLElement)) return
      if (el.closest(SHELL_FOCUS_SKIP_SELECTOR)) return

      const vp = viewportRef.current
      const shell = playerShellRef.current
      const active = document.activeElement
      const onViewport = Boolean(vp && (el === vp || vp.contains(el)))
      const focusInsideShell = Boolean(
        shell && active instanceof Node && shell.contains(active),
      )
      if (onViewport && loadingStage >= 2 && !focusInsideShell) {
        deferPlayPauseRef.current = true
      }

      actions.focusPlayerShell()
    },
    [actions, loadingStage, playerShellRef, viewportRef, deferPlayPauseRef],
  )

  //--------------------------------------------------------------------------------
  return (
    <div
      ref={containerRef}
      className='AevocamPlayer'
      onMouseDownCapture={handleFocusShellFromChromeCapture}
      onPointerDownCapture={handleFocusShellFromChromeCapture}
    >
      {loadingStage >= 1 && (
        <>
          {feed
            ? (
              <div className='Header'>
                <span>
                  {(currentIndex + 1).toLocaleString()} of {totalPhotos.toLocaleString()}
                </span>
                <span className='Separator'>|</span>
                <span>
                  {formattedDateTime}
                </span>
              </div>
            )
            : `Loading... (${totalPhotos.toLocaleString()} photos)`
          }
        </>
      )}

      <AevocamViewport withScrubber />

      <AevocamActionBar />

    </div>
  )
}
